/**
 * Creates and drives the MediaPipe `FaceLandmarker` per the pinned contract
 * URLs/options (see the SHARED CONTRACTS "Serving/ports" section). Pure
 * wiring — the actual per-frame translation into an `EyeSample` lives in
 * `./gaze`, which this module never touches directly, so it can stay free
 * of any mediapipe-specific test setup.
 *
 * `delegate` (`?delegate=gpu`) used to be read directly off
 * `window.location.search` here; it's now parsed once in `../params` and
 * passed in by the caller (`../game`), same defaulting behaviour (unset or
 * anything other than `"gpu"` -> CPU).
 */

import type { FaceLandmarkerResult } from "@mediapipe/tasks-vision";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

const WASM_BASE = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm";
const MODEL_ASSET_PATH =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
/** detectForVideo at most every 33ms (~30fps) — running it every RAF frame is wasted CPU. */
const MIN_DETECT_INTERVAL_MS = 33;

export interface LandmarkerHandle {
  /** Returns null when throttled (no new detection this call) or not yet ready. */
  detect(video: HTMLVideoElement, nowMs: number): FaceLandmarkerResult | null;
  close(): void;
}

let loggedBlendshapeNamesOnce = false;

/** Logs the blendshape category names on the first frame that has any — see api_notes for what this printed (if run in a browser). */
function logBlendshapeNamesOnce(result: FaceLandmarkerResult): void {
  if (loggedBlendshapeNamesOnce) return;
  const categories = result.faceBlendshapes[0]?.categories;
  if (categories === undefined || categories.length === 0) return;
  loggedBlendshapeNamesOnce = true;
  console.log(
    "scarymaze: face blendshape categories (first frame)",
    categories.map((c) => c.categoryName),
  );
}

export async function createLandmarker(delegate: "CPU" | "GPU" = "CPU"): Promise<LandmarkerHandle> {
  const fileset = await FilesetResolver.forVisionTasks(WASM_BASE);
  const landmarker = await FaceLandmarker.createFromOptions(fileset, {
    baseOptions: {
      modelAssetPath: MODEL_ASSET_PATH,
      delegate,
    },
    runningMode: "VIDEO",
    numFaces: 1,
    outputFaceBlendshapes: true,
    outputFacialTransformationMatrixes: true,
  });

  let lastDetectMs = -Infinity;

  return {
    detect(video, nowMs) {
      if (nowMs - lastDetectMs < MIN_DETECT_INTERVAL_MS) return null;
      lastDetectMs = nowMs;
      const result = landmarker.detectForVideo(video, nowMs);
      logBlendshapeNamesOnce(result);
      return result;
    },
    close() {
      landmarker.close();
    },
  };
}
